import { router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { adminInstrumentTypePath, adminInstrumentTypesPath } from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { InstrumentTypeImageUploadSection } from '@/pages/Admin/InstrumentTypes/InstrumentTypeImageUploadSection'

interface InstrumentTypeFormData {
  id: number | null
  name: string
  description: string | null
  rollingCompliancePercent: number | null
  manufacturerId: number | null
  manufacturerModelId: number | null
  hasImage: boolean
  imagePath: string | null
}

interface SelectOption {
  value: number
  label: string
}

interface Props {
  instrumentType: InstrumentTypeFormData | null
  isEdit: boolean
  manufacturerOptions: SelectOption[]
  manufacturerModelOptions: SelectOption[]
  [key: string]: unknown
}

interface FormErrors {
  [key: string]: string[] | undefined
}

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const messages = errors[field]
  if (!messages?.length) return null
  return <p className="text-sm text-destructive mt-1">{messages[0]}</p>
}

const InstrumentTypeForm = () => {
  const { instrumentType, isEdit, manufacturerOptions, manufacturerModelOptions } =
    usePage<Props>().props
  const { errors: pageErrors } = usePage<{ errors?: FormErrors }>().props

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.instrumentTypes'), href: adminInstrumentTypesPath() },
    { label: isEdit ? t('admin.instrumentTypes.editTitle') : t('admin.instrumentTypes.newTitle') },
  ])

  const form = useForm({
    name: instrumentType?.name ?? '',
    description: instrumentType?.description ?? '',
    rollingCompliancePercent: instrumentType?.rollingCompliancePercent?.toString() ?? '',
    manufacturerId: instrumentType?.manufacturerId?.toString() ?? '',
    manufacturerModelId: instrumentType?.manufacturerModelId?.toString() ?? '',
  })

  const canSubmit = useCan(
    Resources.ADMIN_INSTRUMENT_TYPE,
    isEdit ? Actions.UPDATE : Actions.CREATE,
  )

  const errors: FormErrors = (pageErrors as FormErrors | undefined) ?? {}

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({
      instrument_type: {
        ...data,
        manufacturerId: data.manufacturerId || null,
        manufacturerModelId: data.manufacturerModelId || null,
      },
    }))
    if (isEdit && instrumentType?.id) {
      form.patch(adminInstrumentTypePath(instrumentType.id))
    } else {
      form.post(adminInstrumentTypesPath())
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-6">
          {isEdit ? t('admin.instrumentTypes.editTitle') : t('admin.instrumentTypes.newTitle')}
        </h1>

        {errors.base && (
          <div className="mb-4 rounded border border-destructive bg-destructive/10 px-4 py-3">
            {errors.base.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="space-y-1.5">
            <Label htmlFor="name">{t('admin.instrumentTypes.nameField')} *</Label>
            <Input
              id="name"
              value={form.data.name}
              onChange={(e) => form.setData('name', e.target.value)}
              placeholder={t('admin.instrumentTypes.namePlaceholder')}
              required
            />
            <FieldError errors={errors} field="name" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">{t('admin.instrumentTypes.descriptionField')}</Label>
            <Textarea
              id="description"
              value={form.data.description ?? ''}
              onChange={(e) => form.setData('description', e.target.value)}
              rows={3}
            />
            <FieldError errors={errors} field="description" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rollingCompliancePercent">
              {t('admin.instrumentTypes.compliancePercentField')}
            </Label>
            <Input
              id="rollingCompliancePercent"
              type="number"
              min="0"
              max="100"
              value={form.data.rollingCompliancePercent ?? ''}
              onChange={(e) => form.setData('rollingCompliancePercent', e.target.value)}
            />
            <FieldError errors={errors} field="rolling_compliance_percent" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manufacturerId">{t('admin.instrumentTypes.manufacturerField')}</Label>
            <Select
              value={form.data.manufacturerId ?? ''}
              onValueChange={(val) => form.setData('manufacturerId', val)}
            >
              <SelectTrigger id="manufacturerId">
                <SelectValue placeholder={t('admin.instrumentTypes.noManufacturer')} />
              </SelectTrigger>
              <SelectContent>
                {manufacturerOptions.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError errors={errors} field="manufacturer_id" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manufacturerModelId">
              {t('admin.instrumentTypes.manufacturerModelField')}
            </Label>
            <Select
              value={form.data.manufacturerModelId ?? ''}
              onValueChange={(val) => form.setData('manufacturerModelId', val)}
            >
              <SelectTrigger id="manufacturerModelId">
                <SelectValue placeholder={t('admin.instrumentTypes.noModel')} />
              </SelectTrigger>
              <SelectContent>
                {manufacturerModelOptions.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError errors={errors} field="manufacturer_model_id" />
          </div>

          {!canSubmit && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canSubmit && (
              <Button type="submit" disabled={form.processing}>
                {isEdit
                  ? t('admin.instrumentTypes.submitUpdate')
                  : t('admin.instrumentTypes.submitCreate')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => router.visit(adminInstrumentTypesPath())}
            >
              {t('admin.instrumentTypes.cancelButton')}
            </Button>
          </div>
        </form>

        {isEdit && instrumentType?.id != null && (
          <div className="mt-6 max-w-xl">
            <InstrumentTypeImageUploadSection
              instrumentType={{
                id: instrumentType.id,
                hasImage: instrumentType.hasImage,
                imagePath: instrumentType.imagePath,
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default InstrumentTypeForm
